import { useMemo } from 'react';

/**
 * Signups vs. paid conversions over the last 30 days. Pure SVG —
 * no D3 dep — because the data shape is fixed (30 daily points, two
 * series, integer values) and the chart fits comfortably in ~100
 * lines of layout math. Adding a charting library here would mean
 * dragging in 50-300 KB to draw two area curves.
 *
 * Props:
 *   data    — array of { date: "YYYY-MM-DD", signups, conversions }
 *             from /api/admin/stats/signups. Expected length 30 but
 *             the component scales the X-axis to whatever it gets.
 *   loading — show a placeholder strip while the API is pending
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────┐
 *   │ y-tick                                           │
 *   │ ────────── grid                                  │
 *   │ y-tick                                           │
 *   │ ────────── grid           [area curves]          │
 *   │ y-tick                                           │
 *   │ ────────── grid baseline                         │
 *   │ x-tick   x-tick   x-tick   x-tick   x-tick       │
 *   └──────────────────────────────────────────────────┘
 *
 * The chart fills its container width — wrap it in a sized parent.
 *
 * @author <lukman@zshift.io>
 */
const W = 800;        // viewBox width — actual rendered width is responsive
const H = 260;        // viewBox height
const PAD_L = 40;     // left padding (Y-axis labels)
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 28;     // bottom padding (X-axis labels)

const COLOR_SIGNUPS    = '#6B3FD4';   // purple
const COLOR_CONVERSIONS = '#FF6B47';   // brand orange

export function SignupsChart({ data, loading }) {
  // Always derive a non-empty 30-element shape so the empty/loading
  // state renders a flat baseline rather than collapsing the layout.
  const series = useMemo(() => (
    Array.isArray(data) && data.length > 0
      ? data
      : Array.from({ length: 30 }, (_, i) => ({ date: '', signups: 0, conversions: 0 }))
  ), [data]);

  // Y-scale: round the max value up to the next sensible "nice" tick
  // so the chart doesn't draw 0..1.7 (looks weird) — instead 0..2.
  const maxVal = useMemo(() => {
    const peak = series.reduce(
      (m, r) => Math.max(m, r.signups || 0, r.conversions || 0),
      0
    );
    if (peak === 0) return 4;          // empty chart: dummy 4-unit scale
    if (peak <= 2)  return Math.max(2, peak);
    if (peak <= 5)  return 5;
    if (peak <= 10) return 10;
    return Math.ceil(peak / 5) * 5;
  }, [series]);

  // 4 evenly spaced Y ticks including 0 and maxVal.
  const yTicks = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => Math.round((maxVal * i) / steps));
  }, [maxVal]);

  // Inner plot dimensions.
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  // Map data-space coordinates to SVG-space.
  const xOf = (i) => PAD_L + (series.length === 1 ? plotW / 2 : (i * plotW) / (series.length - 1));
  const yOf = (v) => PAD_T + plotH - (v / maxVal) * plotH;

  // Build a smooth-ish line via straight segments (the dataset is
  // small enough that cubic bézier smoothing isn't worth the math).
  const linePath = (key) => series.map((row, i) => {
    const cmd = i === 0 ? 'M' : 'L';
    return `${cmd}${xOf(i).toFixed(1)},${yOf(row[key] || 0).toFixed(1)}`;
  }).join(' ');

  // Closed area path for the filled-area variant. Closes the path
  // back along the X-axis baseline so the fill has a bottom edge.
  const areaPath = (key) => {
    const top = linePath(key);
    const lastX = xOf(series.length - 1).toFixed(1);
    const baselineY = yOf(0).toFixed(1);
    const firstX = xOf(0).toFixed(1);
    return `${top} L${lastX},${baselineY} L${firstX},${baselineY} Z`;
  };

  // X-axis ticks: show 5 evenly spaced dates. Reduces label crowding
  // vs. labelling every day. Format "MM-DD" to fit narrow columns.
  const xTickIdx = [0, 7, 14, 21, 29].filter((i) => i < series.length);
  const formatDate = (iso) => {
    if (!iso) return '';
    // "2026-05-04" → "05-04"
    return iso.length >= 10 ? iso.slice(5) : iso;
  };

  // Totals for the legend chips — show the 30-day sum, much more
  // useful than the y-axis max alone.
  const totalSignups     = series.reduce((s, r) => s + (r.signups     || 0), 0);
  const totalConversions = series.reduce((s, r) => s + (r.conversions || 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Signups vs. paid conversions</h3>
          <div className="sub">DAILY · LAST 30 DAYS</div>
        </div>
        <div className="chart-legend">
          <span className="lg">
            <span className="sw" style={{ background: COLOR_SIGNUPS }}/>
            Signups
            <span style={{ marginLeft: 4, color: 'var(--ink-3)', fontFamily: "'Geist Mono', monospace", fontSize: 11 }}>
              {loading ? '—' : totalSignups.toLocaleString()}
            </span>
          </span>
          <span className="lg">
            <span className="sw" style={{ background: COLOR_CONVERSIONS }}/>
            Conversions
            <span style={{ marginLeft: 4, color: 'var(--ink-3)', fontFamily: "'Geist Mono', monospace", fontSize: 11 }}>
              {loading ? '—' : totalConversions.toLocaleString()}
            </span>
          </span>
        </div>
      </div>
      <div className="chart-body">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          role="img"
          aria-label="Daily signups versus paid conversions over the last 30 days"
        >
          {/* Subtle gradient under each filled area — defs scoped to
              this SVG so they don't collide if the chart is mounted
              twice on the same page. */}
          <defs>
            <linearGradient id="sg1" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor={COLOR_SIGNUPS} stopOpacity="0.28"/>
              <stop offset="100%" stopColor={COLOR_SIGNUPS} stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="sg2" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor={COLOR_CONVERSIONS} stopOpacity="0.28"/>
              <stop offset="100%" stopColor={COLOR_CONVERSIONS} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* Horizontal gridlines + Y-axis tick labels */}
          {yTicks.map((v, i) => {
            const y = yOf(v);
            return (
              <g key={`yt-${i}`}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                      stroke="var(--line)" strokeWidth="1" />
                <text x={PAD_L - 8} y={y + 3}
                      textAnchor="end"
                      fontFamily="'Geist Mono', monospace"
                      fontSize="10"
                      fill="var(--ink-3)">
                  {v}
                </text>
              </g>
            );
          })}

          {/* Filled areas — Signups under, Conversions on top */}
          {!loading && (
            <>
              <path d={areaPath('signups')}     fill="url(#sg1)" />
              <path d={linePath('signups')}     fill="none" stroke={COLOR_SIGNUPS}    strokeWidth="2" />
              <path d={areaPath('conversions')} fill="url(#sg2)" />
              <path d={linePath('conversions')} fill="none" stroke={COLOR_CONVERSIONS} strokeWidth="2" />
            </>
          )}

          {/* X-axis tick labels */}
          {xTickIdx.map((i) => (
            <text key={`xt-${i}`}
                  x={xOf(i)} y={H - 8}
                  textAnchor="middle"
                  fontFamily="'Geist Mono', monospace"
                  fontSize="10"
                  fill="var(--ink-3)">
              {formatDate(series[i].date)}
            </text>
          ))}

          {loading && (
            <text x={W / 2} y={H / 2}
                  textAnchor="middle"
                  fontFamily="Geist, sans-serif"
                  fontSize="13"
                  fill="var(--ink-3)">
              Loading…
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}
